import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  faArrowRight,
  faInfoCircle,
  faTableLayout,
} from "@fortawesome/pro-regular-svg-icons";
import { Controller, UseFormReturn, useForm, useWatch } from "react-hook-form";

import { ErrorBaseInfo } from "src/api/types";
import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Pill, PillVariants } from "src/base-components/Pill";
import { Select } from "src/base-components/Select";
import { Spinner } from "src/base-components/Spinner";
import { EnumAssignmentCard } from "src/caseCreationNode/EnumAssignmentCard";
import { StatusAssignmentCard } from "src/caseCreationNode/StatusAssignmentCard";
import { CaseTemplateDropdown } from "src/cases/common/CaseTemplateDropdown";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import { useCaseQueue, useCaseQueues } from "src/cases/queries/queues";
import { useCaseTemplatesWithVersions } from "src/cases/queries/templates";
import { DeserializedCaseQueue } from "src/cases/queues/types";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import {
  CasePropertiesValue,
  CaseTemplateAdditionalInput,
  CaseTemplateFileCategoryDefinition,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
} from "src/clients/case-management";
import {
  CaseCreationNodeMeta,
  CaseCreationNodeMetaAssignedQueueTypeEnum,
  CaseCreationNodeMetaMappedValueSectionEnum,
  CaseCreationNodeMetaMappedValueTypeEnum,
  CaseCreationNodeMetaMappedValueValueTypeEnum,
} from "src/clients/flow-api";
import {
  CaseCreationNode,
  CaseCreationNodeData,
} from "src/constants/NodeDataTypes";
import { Accordion } from "src/design-system/Accordion";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import {
  useCanvasSearchHighlight,
  useIsFieldHighlighted,
} from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { useSubmitForm } from "src/utils/useSubmitForm";

const FIELDS_WITH_THEIR_OWN_SECTION = new Set([
  "source",
  "priority",
  "status",
  "sub_status",
]);
const STATUS_FIELDS = new Set(["status", "sub_status"]);
const MAX_PREDEFINED_VALUES_TO_SHOW = 10;

type PropertyType =
  | CasePropertiesValue
  | CaseTemplateAdditionalInput
  | CaseTemplateFileCategoryDefinition;

const getValuesHelpText = (property: PropertyType): React.ReactNode => {
  if ("type" in property) {
    if (property.type === "enum" || property.type === "tags") {
      const values = property.values;

      if (values.length <= 0) return null;

      const displayedValues = values.slice(0, MAX_PREDEFINED_VALUES_TO_SHOW);
      const remainingCount = values.length - MAX_PREDEFINED_VALUES_TO_SHOW;

      return (
        <div className="space-y-1">
          <p className="font-inter-semibold-12px">Supported values:</p>
          <div className="flex flex-wrap gap-1">
            {displayedValues.map((v) => (
              <Pill key={v.value} size="sm" variant="dark-gray">
                <Pill.Text fontType="code">"{v.value}"</Pill.Text>
              </Pill>
            ))}
            {remainingCount > 0 && (
              <Pill size="sm" variant="dark-gray">
                <Pill.Text fontType="code">+ {remainingCount} more</Pill.Text>
              </Pill>
            )}
          </div>
        </div>
      );
    }

    if (property.type === "entity_ref") {
      return (
        <>
          This property expects an{" "}
          <Pill size="sm" variant="dark-gray">
            <Pill.Text fontType="code">entity ID</Pill.Text>
          </Pill>{" "}
          as input.
        </>
      );
    }

    if (property.type === "event_ref") {
      return (
        <>
          This property expects an{" "}
          <Pill size="sm" variant="dark-gray">
            <Pill.Text fontType="code">event ID</Pill.Text>
          </Pill>{" "}
          as input.
        </>
      );
    }

    if (property.type === "integer") {
      return (
        <>
          This property expects an{" "}
          <Pill size="sm" variant="dark-gray">
            <Pill.Text fontType="code">integer</Pill.Text>
          </Pill>{" "}
          as input.
        </>
      );
    }

    if (property.type === "number") {
      return (
        <>
          This property expects a{" "}
          <Pill size="sm" variant="dark-gray">
            <Pill.Text fontType="code">number</Pill.Text>
          </Pill>{" "}
          as input.
        </>
      );
    }
  }
  return null;
};

const DECISION_FLOW_SOURCE_KEY = "decision_flow";

const resolveSourceValue = (
  currentValue: string,
  sourceProperty: CasePropertiesValue | undefined,
): string => {
  if (sourceProperty?.type !== "enum") return "";

  if (
    currentValue &&
    sourceProperty.values.some((v) => v.value === currentValue)
  ) {
    return currentValue;
  }

  const hasDecisionFlow = sourceProperty.values.some(
    (v) => v.value === DECISION_FLOW_SOURCE_KEY,
  );
  return hasDecisionFlow ? DECISION_FLOW_SOURCE_KEY : "";
};

type MappingEntry = CaseCreationNodeMeta["details_mapping"][string];

const makeMappingEntry = (
  valueType: CaseCreationNodeMetaMappedValueValueTypeEnum,
  value: string,
  section: CaseCreationNodeMetaMappedValueSectionEnum,
): MappingEntry => ({
  type: CaseCreationNodeMetaMappedValueTypeEnum.NON_CONDITIONAL,
  value_type: valueType,
  value,
  section,
});

/**
 * Resolves status and sub_status mapping when switching templates.
 *
 * - Dynamic → always keep previous values as-is
 * - Fixed → check if old status (+ sub_status) exists in new config:
 *     valid → preserve; invalid → clear both
 */
export const resolveStatusAndSubStatus = (
  statusProperty: CasePropertiesValue,
  previousMapping: CaseCreationNodeMeta["details_mapping"],
): Record<string, MappingEntry> => {
  const previousStatus = previousMapping.status;
  const valueType =
    previousStatus?.value_type ??
    CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED;

  const makeStatusEntry = (value: string) =>
    makeMappingEntry(
      valueType,
      value,
      CaseCreationNodeMetaMappedValueSectionEnum.PROPERTIES,
    );

  const previousStatusValue = previousStatus?.value ?? "";
  const previousSubStatusValue = previousMapping.sub_status?.value ?? "";

  // If it was dynamic, we just return the previous values as is
  if (valueType === CaseCreationNodeMetaMappedValueValueTypeEnum.DYNAMIC) {
    return {
      status: makeStatusEntry(previousStatusValue),
      sub_status: makeStatusEntry(previousSubStatusValue),
    };
  }

  const isValid = isPreviousStatusValidForProperty(
    statusProperty,
    previousStatusValue,
    previousSubStatusValue,
  );

  return {
    status: makeStatusEntry(isValid ? previousStatusValue : ""),
    sub_status: makeStatusEntry(isValid ? previousSubStatusValue : ""),
  };
};

export const isPreviousStatusValidForProperty = (
  statusProperty: CasePropertiesValue,
  statusValue: string,
  subStatusValue: string,
): boolean => {
  // This should never happen, but just in case
  if (statusProperty.type !== "enum") return false;

  const matchingStatus = statusProperty.values.find(
    (v) => v.value === statusValue,
  );

  if (!matchingStatus) return false;

  const hasSubStatuses = statusProperty.values.some(
    (v) => v.sub_values && v.sub_values.length > 0,
  );

  // No sub-statuses in config and no old sub_status value → status match is enough
  if (!hasSubStatuses || !subStatusValue) return true;

  // Has sub-statuses → need a valid sub_status too
  return (
    matchingStatus.sub_values?.some((sv) => sv.value === subStatusValue) ??
    false
  );
};

export const getUpdatedDetailsMapping = (
  currentValue: CaseCreationNodeMeta["details_mapping"],
  selectedTemplateVersion: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
) => {
  const { case_properties, additional_inputs, file_categories } =
    selectedTemplateVersion.attributes;

  const propertyEntries = Object.keys({
    ...case_properties,
    ...additional_inputs,
  })
    .filter((key) => !STATUS_FIELDS.has(key))
    .map((key) => {
      const value_type = FIELDS_WITH_THEIR_OWN_SECTION.has(key)
        ? CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED
        : CaseCreationNodeMetaMappedValueValueTypeEnum.DYNAMIC;
      const section =
        key in case_properties
          ? CaseCreationNodeMetaMappedValueSectionEnum.PROPERTIES
          : CaseCreationNodeMetaMappedValueSectionEnum.ADDITIONAL;
      const shouldPreserveValue =
        currentValue[key] &&
        currentValue[key].value_type === value_type &&
        currentValue[key].section === section;

      const resolvedValue = shouldPreserveValue ? currentValue[key].value : "";

      const value =
        key === "source"
          ? resolveSourceValue(resolvedValue, case_properties.source)
          : resolvedValue;

      return [key, makeMappingEntry(value_type, value, section)];
    });

  const fileCategoryEntries = Object.keys(file_categories ?? {}).map((key) => [
    key,
    makeMappingEntry(
      CaseCreationNodeMetaMappedValueValueTypeEnum.DYNAMIC,
      currentValue[key]?.value || "",
      CaseCreationNodeMetaMappedValueSectionEnum.FILE_CATEGORIES,
    ),
  ]);

  const updatedDetailsMapping: CaseCreationNodeMeta["details_mapping"] =
    Object.fromEntries([...propertyEntries, ...fileCategoryEntries]);

  if (case_properties.status) {
    const statusEntries = resolveStatusAndSubStatus(
      case_properties.status,
      currentValue,
    );
    Object.assign(updatedDetailsMapping, statusEntries);
  }

  return updatedDetailsMapping;
};

const isTemplateRelatedToQueue = (
  templateId: string,
  queue?: DeserializedCaseQueue,
) => {
  const templateIds =
    queue?.template_configs.map(({ template }) => template.id) ?? [];
  return templateIds?.some((id) => id === templateId);
};

type Props = {
  node: CaseCreationNode;
  displayedError: ErrorBaseInfo | undefined;
} & NodeEditorBaseProps<CaseCreationNodeData>;

enum QueueErrorTag {
  IsNotAnExistingQueue = "is_not_an_existing_queue",
  IsUnsupportedQueue = "is_unsupported_queue",
}

const CaseMappings = ({
  nodeId,
  formMethods,
  immutable,
  allProperties,
  propertyKey,
  isFileCategory: isFileCategoryField,
}: {
  nodeId: string;
  propertyKey: string;
  formMethods: UseFormReturn<CaseCreationNodeData>;
  isFileCategory?: boolean;
  immutable: boolean;
  allProperties: Record<string, PropertyType>;
}) => {
  const property = allProperties?.[propertyKey];

  const getPropertyIcon = useGetCasePropertyIcon();
  const isSearchHighlighted = useIsFieldHighlighted(`${nodeId}:${propertyKey}`);

  if (!property) return null;

  const description = isFileCategoryField
    ? (property as CaseTemplateFileCategoryDefinition).description
    : null;

  const helpText = description || getValuesHelpText(property);

  const icon = getPropertyIcon(property);

  return (
    <div
      className="flex w-full items-center gap-2"
      data-loc={`case-creation-mapping-${propertyKey}`}
    >
      <div className="w-1/2">
        <Controller
          control={formMethods.control}
          name={`details_mapping.${propertyKey}.value`}
          render={({ field }) => (
            <AutocompleteCodeInput
              dataLoc="case-creation-mapping-value"
              disabled={immutable}
              error={
                !!formMethods.formState.errors.details_mapping?.[propertyKey]
              }
              isSearchHighlighted={isSearchHighlighted}
              placeholder={isFileCategoryField ? "data.files[]" : "data.field"}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
      <div className="flex-shrink-0">
        <Icon icon={faArrowRight} size="xs" />
      </div>
      <div className="w-1/2">
        <Input
          placeholder={property.display_name ?? propertyKey}
          prefixIcon={{ icon }}
          suffixIcon={
            helpText
              ? {
                  icon: faInfoCircle,
                  helpText,
                }
              : undefined
          }
          disabled
          fullWidth
        />
      </div>
    </div>
  );
};

export const CaseCreationNodeEditor: React.FC<Props> = ({
  immutable,
  node,
  isReactive,
  onUpdate,
  displayedError,
}) => {
  const { highlightedMatch } = useCanvasSearchHighlight();
  const { renderedInDiffView } = useDiffViewContext();
  const isHighlightedNode = highlightedMatch?.nodeId === node.id;
  const [accordionValue, setAccordionValue] = useState<string[]>([
    "case-details",
  ]);

  const effectiveAccordionValue =
    isHighlightedNode || renderedInDiffView
      ? [
          "case-details",
          "assign-queue",
          "assign-priority",
          "assign-status",
          "assign-source",
          "configure-case-input-location",
        ]
      : accordionValue;

  const templateDropdownButtonRef = useRef<HTMLDivElement | null>(null);

  const formMethods = useForm<CaseCreationNodeData>({
    mode: "onBlur",
    shouldFocusError: false,
    defaultValues: node.data,
    ...(isReactive && { values: node.data }),
  });
  const isCaseInputLocationHighlighted = useIsFieldHighlighted(
    `${node.id}:case_input_location`,
  );
  useSubmitForm({
    onChange: (data: CaseCreationNodeData) => {
      onUpdate({ newData: data });
    },
    previousValues: node.data,
    disabled: isReactive,
    watch: formMethods.watch,
  });

  useEffect(() => {
    if (displayedError) {
      if (displayedError.loc_type === "field") {
        formMethods.setError(displayedError.loc as keyof CaseCreationNodeData, {
          message: displayedError.msg as string,
        });
      } else if (
        displayedError.loc_type === "node" &&
        displayedError.field_errors?.length
      ) {
        displayedError.field_errors.forEach((fieldError) => {
          formMethods.setError(
            `details_mapping.${fieldError.field_name}` as keyof CaseCreationNodeData,
            {
              message: fieldError.message as string,
            },
          );
        });
      }
    }
  }, [displayedError, formMethods]);

  const currentTemplateValue = useWatch({
    control: formMethods.control,
    name: "template",
  });

  const currentDetailsMapping = useWatch({
    control: formMethods.control,
    name: "details_mapping",
  });

  const { data: templates } = useCaseTemplatesWithVersions();
  const { data: queues } = useCaseQueues();
  const { data: queue } = useCaseQueue(node.data.assigned_queue?.value);

  const selectedTemplateVersion = (() => {
    if (!currentTemplateValue?.id || !currentTemplateValue?.version_id) {
      return undefined;
    }

    const template = templates?.find((t) => t.id === currentTemplateValue.id);
    const version = template?.versions?.find(
      (v) => v.id === currentTemplateValue.version_id,
    );

    if (template && version) {
      return {
        template,
        version,
      };
    }

    return undefined;
  })();

  const templateHasSubStatuses = useMemo(() => {
    if (!selectedTemplateVersion) return false;

    const statusProperty =
      selectedTemplateVersion?.version.attributes.case_properties.status;
    return (
      statusProperty?.type === "enum" &&
      statusProperty.values.some(
        (value) => value.sub_values && value.sub_values.length > 0,
      )
    );
  }, [selectedTemplateVersion]);

  // If a template version changes after its been used to configure
  // this node, we need to update the details mapping
  useEffect(() => {
    if (
      currentTemplateValue &&
      selectedTemplateVersion &&
      String(currentTemplateValue.etag) !==
        String(selectedTemplateVersion.version.attributes.etag ?? "0")
    ) {
      const updatedDetailsMapping = getUpdatedDetailsMapping(
        currentDetailsMapping,
        selectedTemplateVersion.version,
      );
      formMethods.setValue("details_mapping", updatedDetailsMapping);
      formMethods.setValue(
        "template.etag",
        parseInt(selectedTemplateVersion.version.attributes.etag ?? "0", 10),
      );
    }
  }, [
    formMethods,
    currentDetailsMapping,
    currentTemplateValue,
    selectedTemplateVersion,
  ]);

  const queueErrorTag = useMemo(() => {
    if (!node.data.assigned_queue?.value || !node.data.template?.id || !queue)
      return;

    const selectedQueue = queues?.find(
      (queue) => queue.id === node.data.assigned_queue?.value,
    );

    if (!selectedQueue) return QueueErrorTag.IsNotAnExistingQueue;

    if (!isTemplateRelatedToQueue(node.data.template?.id, queue)) {
      return QueueErrorTag.IsUnsupportedQueue;
    }
  }, [node.data.assigned_queue, node.data.template?.id, queues, queue]);

  const sourceProperty =
    selectedTemplateVersion?.version.attributes.case_properties.source;

  const sourceOptions = useMemo(() => {
    if (!sourceProperty || sourceProperty.type !== "enum") return undefined;
    return sourceProperty.values.map((v) => ({
      value: (
        <Pill size="sm" variant={(v.color as PillVariants) ?? "gray"}>
          <Pill.Text>{v.display_name || v.value}</Pill.Text>
        </Pill>
      ),
      key: v.value,
    }));
  }, [sourceProperty]);

  if (!templates || !queues) {
    return <Spinner />;
  }

  const sectionEmptyState = (
    <div className="rounded border">
      <EmptyState
        actionButton={{
          label: "Select template",
          onClick: () => {
            setAccordionValue(["case-details"]);
            templateDropdownButtonRef.current?.click();
          },
        }}
        description="Select the template you want to use to complete configuration"
        headline="No template selected"
        icon={faTableLayout}
      />
    </div>
  );

  const allProperties = {
    ...selectedTemplateVersion?.version.attributes.case_properties,
    ...selectedTemplateVersion?.version.attributes.additional_inputs,
    ...selectedTemplateVersion?.version.attributes.file_categories,
  };

  const casePropertiesKeys = Object.keys(currentDetailsMapping)
    .filter(
      (key) =>
        !FIELDS_WITH_THEIR_OWN_SECTION.has(key) &&
        (currentDetailsMapping[key]?.section ===
          CaseCreationNodeMetaMappedValueSectionEnum.PROPERTIES ||
          currentDetailsMapping[key]?.section ===
            CaseCreationNodeMetaMappedValueSectionEnum.FILE_CATEGORIES),
    )
    .sort((a, b) => a.localeCompare(b));

  const additionalInputsKeys = Object.keys(currentDetailsMapping)
    .filter(
      (key) =>
        currentDetailsMapping[key]?.section ===
        CaseCreationNodeMetaMappedValueSectionEnum.ADDITIONAL,
    )
    .sort((a, b) => a.localeCompare(b));

  const priorityProperty =
    selectedTemplateVersion?.version.attributes.case_properties.priority;
  const statusProperty =
    selectedTemplateVersion?.version.attributes.case_properties.status;

  const thereIsMappingToDisplay =
    selectedTemplateVersion &&
    (casePropertiesKeys.length > 0 || additionalInputsKeys.length > 0);

  return (
    <div className="flex flex-col gap-x-4">
      {queueErrorTag && (
        <Callout variant="error">
          {queueErrorTag === QueueErrorTag.IsNotAnExistingQueue &&
            "Assigned queue was not found. Please reconfigure mapping to another queue."}
          {queueErrorTag === QueueErrorTag.IsUnsupportedQueue &&
            "Selected queue does not support the template"}
        </Callout>
      )}
      <Accordion value={effectiveAccordionValue} onChange={setAccordionValue}>
        <Accordion.Item
          isReadOnly={renderedInDiffView}
          label="Configure case details"
          value="case-details"
        >
          <Card className="w-full pt-4 pb-4">
            <FormItem>
              <FormItem.Label className="mb-2" isRequired>
                Case template
              </FormItem.Label>
              <Controller
                control={formMethods.control}
                name="template"
                render={({ field }) => (
                  <CaseTemplateDropdown
                    buttonRef={templateDropdownButtonRef}
                    dataLoc="create-case-template-dropdown"
                    disabled={immutable}
                    error={!!formMethods.formState.errors.template}
                    options={templates}
                    placeholder="Select a template and version"
                    selected={
                      selectedTemplateVersion
                        ? {
                            templateId: selectedTemplateVersion.template.id,
                            versionId: selectedTemplateVersion.version.id,
                          }
                        : undefined
                    }
                    onSelect={(selection) => {
                      const template = templates.find(
                        (t) => t.id === selection.templateId,
                      );
                      const version = template?.versions?.find(
                        (v) => v.id === selection.versionId,
                      );

                      if (template && version) {
                        field.onChange({
                          id: template.id,
                          version_id: version.id,
                          etag: parseInt(version.attributes.etag ?? "0", 10),
                        });
                        formMethods.setValue(
                          "details_mapping",
                          getUpdatedDetailsMapping(
                            currentDetailsMapping,
                            version,
                          ),
                        );
                      }
                    }}
                  />
                )}
              />
            </FormItem>
          </Card>
          {thereIsMappingToDisplay && (
            <Card className="mt-3 w-full space-y-4 pt-4 pb-4">
              {/* Case Properties Section (includes file categories) */}
              {casePropertiesKeys.length > 0 && (
                <div className="space-y-3">
                  <div>
                    <p className="font-inter-semibold-13px text-gray-800">
                      Map case properties and files
                    </p>
                    <p className="font-inter-normal-12px text-gray-500">
                      Map the data from this Decision Flow to the case template
                      properties and file categories
                    </p>
                  </div>
                  <div className="space-y-2">
                    {casePropertiesKeys.map((key) => (
                      <CaseMappings
                        key={key}
                        allProperties={allProperties}
                        formMethods={formMethods}
                        immutable={immutable}
                        isFileCategory={Boolean(
                          selectedTemplateVersion?.version.attributes
                            .file_categories?.[key],
                        )}
                        nodeId={node.id}
                        propertyKey={key}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Inputs Section */}
              {additionalInputsKeys.length > 0 && (
                <div className="space-y-3">
                  <div>
                    <p className="font-inter-semibold-13px text-gray-800">
                      Map case block inputs
                    </p>
                    <p className="font-inter-normal-12px text-gray-500">
                      Map the data from this Decision Flow to the case blocks
                    </p>
                  </div>
                  <div className="space-y-2">
                    {additionalInputsKeys.map((key) => (
                      <CaseMappings
                        key={key}
                        allProperties={allProperties}
                        formMethods={formMethods}
                        immutable={immutable}
                        nodeId={node.id}
                        propertyKey={key}
                      />
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
        </Accordion.Item>
        <Accordion.Item
          isReadOnly={renderedInDiffView}
          label="Assign queue"
          value="assign-queue"
        >
          {selectedTemplateVersion ? (
            <Card className="w-full pt-4 pb-4">
              <FormItem>
                <FormItem.Label className="mb-2" isRequired>
                  Queue
                </FormItem.Label>
                <Controller
                  control={formMethods.control}
                  name="assigned_queue"
                  render={({ field }) => (
                    <Select
                      buttonRenderer={(option) => (
                        <div data-loc="case-creation-queue-select-value">
                          {option?.value}
                        </div>
                      )}
                      dataLoc="case-creation-queue-select"
                      disabled={immutable}
                      errored={!!formMethods.formState.errors.assigned_queue}
                      options={queues?.map((queue) => ({
                        value: (
                          <Pill size="sm" variant="gray">
                            <Pill.Text>{queue.name}</Pill.Text>
                          </Pill>
                        ),
                        key: queue.id,
                      }))}
                      placeholder="Select the queue"
                      value={field.value?.value}
                      onChange={(value) => {
                        field.onChange({
                          type: CaseCreationNodeMetaAssignedQueueTypeEnum.NON_CONDITIONAL,
                          value_type: "fixed",
                          value,
                        });
                      }}
                    />
                  )}
                />
              </FormItem>
            </Card>
          ) : (
            sectionEmptyState
          )}
        </Accordion.Item>
        {priorityProperty && (
          <Accordion.Item
            isReadOnly={renderedInDiffView}
            label="Assign priority"
            value="assign-priority"
          >
            {selectedTemplateVersion ? (
              <Controller
                control={formMethods.control}
                name="details_mapping.priority"
                render={({ field }) => (
                  <EnumAssignmentCard
                    error={
                      !!formMethods.formState.errors.details_mapping?.priority
                    }
                    fieldName="priority"
                    immutable={immutable}
                    nodeId={node.id}
                    property={priorityProperty}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            ) : (
              sectionEmptyState
            )}
          </Accordion.Item>
        )}
        <Accordion.Item
          isReadOnly={renderedInDiffView}
          label="Assign status"
          value="assign-status"
        >
          {selectedTemplateVersion && statusProperty ? (
            <Controller
              control={formMethods.control}
              name="details_mapping.status"
              render={({ field }) => {
                const subStatusFormValue = formMethods.getValues(
                  "details_mapping.sub_status",
                );
                return (
                  <StatusAssignmentCard
                    enableSubStatus={templateHasSubStatuses}
                    error={
                      !!formMethods.formState.errors.details_mapping?.status ||
                      !!formMethods.formState.errors.details_mapping?.sub_status
                    }
                    fieldName="status"
                    immutable={immutable}
                    nodeId={node.id}
                    property={
                      statusProperty as Parameters<
                        typeof StatusAssignmentCard
                      >[0]["property"]
                    }
                    status={field.value.value}
                    subStatus={subStatusFormValue?.value}
                    valueType={field.value.value_type}
                    onChangeValue={(status, subStatus) => {
                      field.onChange({
                        ...field.value,
                        value: status,
                      });

                      if (subStatus) {
                        formMethods.setValue("details_mapping.sub_status", {
                          ...field.value,
                          value: subStatus,
                        });
                      } else {
                        formMethods.unregister("details_mapping.sub_status");
                      }
                    }}
                    onChangeValueType={(valueType) => {
                      field.onChange({
                        ...field.value,
                        value: "",
                        value_type: valueType,
                      });

                      const subStatus = formMethods.getValues(
                        "details_mapping.sub_status",
                      );
                      // Only reset the sub_status value_type if it was already set
                      if (subStatus) {
                        formMethods.setValue("details_mapping.sub_status", {
                          ...field.value,
                          value: "",
                          value_type: valueType,
                        });
                      }
                    }}
                  />
                );
              }}
            />
          ) : (
            sectionEmptyState
          )}
        </Accordion.Item>
        {sourceOptions && (
          <Accordion.Item
            isReadOnly={renderedInDiffView}
            label="Assign source"
            value="assign-source"
          >
            {selectedTemplateVersion ? (
              <Card className="w-full pt-4 pb-4">
                <FormItem>
                  <FormItem.Label className="mb-2">Source</FormItem.Label>
                  <Controller
                    control={formMethods.control}
                    name="details_mapping.source"
                    render={({ field }) => (
                      <Select
                        buttonRenderer={(option) => (
                          <div data-loc="case-creation-source-select-value">
                            {option?.value}
                          </div>
                        )}
                        dataLoc="case-creation-source-select"
                        disabled={immutable}
                        errored={
                          !!formMethods.formState.errors.details_mapping?.source
                        }
                        options={sourceOptions}
                        placeholder="Select source"
                        value={field.value?.value || null}
                        fullWidth
                        onChange={(value) => {
                          field.onChange({
                            ...field.value,
                            value,
                            value_type:
                              CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED,
                          });
                        }}
                      />
                    )}
                  />
                </FormItem>
              </Card>
            ) : (
              sectionEmptyState
            )}
          </Accordion.Item>
        )}
        <Accordion.Item
          isReadOnly={renderedInDiffView}
          label="Configure case input location"
          value="configure-case-input-location"
        >
          <p className="mb-4 font-inter-normal-12px text-gray-500">
            Case input includes all the fields configured above — such as case
            details, queue, status, and priority. These values will be stored in
            the location you specify and made available to downstream nodes.
          </p>
          <Card className="w-full">
            <FormItem>
              <FormItem.Label className="mb-2">
                Select location for case input
              </FormItem.Label>
              <Controller
                control={formMethods.control}
                name="case_input_location"
                render={({ field }) => (
                  <AutocompleteCodeInput
                    dataLoc="case-creation-input-location"
                    disabled={immutable}
                    isSearchHighlighted={isCaseInputLocationHighlighted}
                    placeholder="field"
                    prefix="data."
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </FormItem>
          </Card>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};
